/**
 * Component: ZeroTouchCard
 * Purpose: the "Zero-touch scheduling" control-plane card on an installation
 * (tracker #56 slice 2, scheduler design §6.2). Opt-in, the two-step grant
 * guidance (admin consent of the platform app's Sites.Selected application
 * permission, then a WRITE grant on the audit site ONLY — exact site URL +
 * copy-paste customer-admin Graph/PowerShell snippets), the canary-write probe
 * (D3), the grant-state pill, and revocation.
 *
 * The card is self-contained: it loads its own state from
 * GET /api/zero-touch/state and every mutation is entitlement-gated
 * SERVER-side — this UI never decides anything security-relevant.
 */
import React, { useCallback, useEffect, useState } from 'react';

type ZeroTouchGrantStatus = 'not_configured' | 'granted' | 'revoked' | 'degraded';

interface ZeroTouchGrantView {
  grantStatus: ZeroTouchGrantStatus;
  siteUrl?: string;
  optedInAt?: string;
  optedInBy?: string;
  grantedAt?: string;
  lastProbeAt?: string;
  lastProbeSucceededAt?: string;
  consecutiveProbeFailures: number;
  lastProbeError?: string;
  revokedAt?: string;
  revokedBy?: string;
  revokedReason?: 'portal' | 'm365_probe';
  permissionId?: string;
}

interface ZeroTouchGuidance {
  siteUrl: string;
  appClientId: string;
  appDisplayName: string;
  adminConsentUrl: string;
  powershellSnippet: string;
  graphSnippet: string;
  revokePowershellSnippet: string;
}

interface ZeroTouchStateResponse {
  enabled: boolean;
  entitled: boolean;
  grant: ZeroTouchGrantView | null;
  guidance: ZeroTouchGuidance | null;
}

const STATUS_LABEL: Record<ZeroTouchGrantStatus, string> = {
  not_configured: 'Not configured',
  granted: 'Granted — server-side creation active',
  degraded: 'Degraded — probe failing, paused',
  revoked: 'Revoked — paused'
};

const STATUS_CLASS: Record<ZeroTouchGrantStatus, string> = {
  not_configured: 'pill',
  granted: 'pill pill-success',
  degraded: 'pill pill-warning',
  revoked: 'pill pill-danger'
};

interface ZeroTouchCardProps {
  tenantId: string;
  instanceId: string;
}

const ZeroTouchCard: React.FC<ZeroTouchCardProps> = ({ tenantId, instanceId }) => {
  const [state, setState] = useState<ZeroTouchStateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [showGuidance, setShowGuidance] = useState(false);
  const [permissionId, setPermissionId] = useState('');

  const load = useCallback(async (): Promise<void> => {
    try {
      const params = new URLSearchParams({ tenantId, instanceId });
      const response = await fetch(`/api/zero-touch/state?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Failed to load zero-touch state (${response.status})`);
      }
      const next = await response.json() as ZeroTouchStateResponse;
      setState(next);
      setPermissionId(next.grant?.permissionId ?? '');
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [tenantId, instanceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const runAction = async (
    action: 'opt-in' | 'probe' | 'revoke',
    confirmText?: string
  ): Promise<void> => {
    if (confirmText && !window.confirm(confirmText)) {
      return;
    }
    setBusy(action);
    setNotice(null);
    setError(null);
    try {
      const response = await fetch(`/api/zero-touch/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, instanceId })
      });
      const payload = await response.json().catch(() => ({})) as {
        ok?: boolean; error?: { message?: string }; message?: string; probeError?: string;
      };
      if (!response.ok) {
        throw new Error(payload?.error?.message || payload?.message || `Request failed (${response.status})`);
      }
      if (action === 'probe') {
        setNotice(payload.ok
          ? 'Write probe succeeded — zero-touch creation is active for this installation.'
          : `Probe did not succeed: ${payload.probeError ?? 'see grant status for details.'}`);
      } else if (action === 'opt-in') {
        setNotice('Opted in. Complete the two grant steps below, then verify with the write probe.');
        setShowGuidance(true);
      } else {
        setNotice('Zero-touch scheduling revoked in Edinshall. Server-side creation is stopped; complete the separate Microsoft 365 removal step shown below.');
        setShowGuidance(true);
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const recordAssignment = async (): Promise<void> => {
    setBusy('record-assignment');
    setError(null);
    setNotice(null);
    try {
      const response = await fetch('/api/zero-touch/record-assignment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId, instanceId, permissionId })
      });
      const payload = await response.json().catch(() => ({})) as { message?: string };
      if (!response.ok) throw new Error(payload.message || `Request failed (${response.status})`);
      setNotice('Site assignment identity recorded. Verify it with the write probe.');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (!state) {
    return null;
  }

  if (!state.enabled) {
    return null;
  }

  if (!state.entitled) {
    return (
      <div className="installation-action-group">
        <div className="group-label">Zero-touch scheduling</div>
        <p className="muted" style={{ fontSize: 12 }}>
          Server-side creation of scheduled audits requires a Standard or Enterprise plan.
          Every plan already gets due reminders, escalations and dashboard auto-creation.
        </p>
      </div>
    );
  }

  const grant = state.grant ?? { grantStatus: 'not_configured' as ZeroTouchGrantStatus, consecutiveProbeFailures: 0 };
  const guidance = state.guidance;
  const optedIn = Boolean(grant.optedInAt);

  return (
    <div className="installation-action-group">
      <div className="group-label">
        Zero-touch scheduling{' '}
        <span className={STATUS_CLASS[grant.grantStatus]}>{STATUS_LABEL[grant.grantStatus]}</span>
      </div>
      <p className="muted" style={{ fontSize: 12 }}>
        With the customer&apos;s consent, the platform creates due scheduled audits directly in the audit
        site — no one has to open the dashboard. Requires the two-step Sites.Selected write grant below;
        the write surface is creation of scheduled audits plus a probe item only.
      </p>
      {grant.grantStatus === 'degraded' && (
        <div className="muted" style={{ color: 'var(--danger)', fontSize: 12 }}>
          The write probe is failing ({grant.consecutiveProbeFailures} consecutive failures). Server-side
          creation is paused; v1 reminder e-mails and dashboard auto-creation continue.
          {grant.lastProbeError ? ` Last error: ${grant.lastProbeError}` : ''}
        </div>
      )}
      {grant.grantStatus === 'revoked' && (
        <div className="muted" style={{ color: 'var(--danger)', fontSize: 12 }}>
          {grant.revokedReason === 'm365_probe'
            ? 'The grant was revoked in Microsoft 365 (detected by the failing write probe). Re-grant the site and verify to resume.'
            : `Revoked from this portal${grant.revokedBy ? ` by ${grant.revokedBy}` : ''}. Opt in again to restart the grant flow.`}
        </div>
      )}
      <div className="group-actions">
        {!optedIn && (
          <button className="btn" type="button" disabled={busy !== null} onClick={() => void runAction('opt-in')}>
            {busy === 'opt-in' ? 'Opting in…' : 'Opt in'}
          </button>
        )}
        {optedIn && grant.grantStatus !== 'revoked' && (
          <>
            <button className="btn secondary" type="button" onClick={() => setShowGuidance(current => !current)}>
              {showGuidance ? 'Hide grant steps' : 'Show grant steps'}
            </button>
            <button className="btn" type="button" disabled={busy !== null || !grant.permissionId} onClick={() => void runAction('probe')}>
              {busy === 'probe' ? 'Probing…' : 'Verify grant (write probe)'}
            </button>
            <button
              className="btn secondary"
              type="button"
              disabled={busy !== null}
              onClick={() => void runAction(
                'revoke',
                'Revoke zero-touch scheduling for this installation? Server-side audit creation stops immediately; v1 reminders and dashboard auto-creation continue.'
              )}
            >
              {busy === 'revoke' ? 'Revoking…' : 'Revoke'}
            </button>
          </>
        )}
        {optedIn && grant.grantStatus === 'revoked' && grant.revokedReason === 'portal' && (
          <button className="btn" type="button" disabled={busy !== null} onClick={() => void runAction('opt-in')}>
            {busy === 'opt-in' ? 'Opting in…' : 'Opt in again'}
          </button>
        )}
        {optedIn && grant.grantStatus === 'revoked' && grant.revokedReason === 'm365_probe' && (
          <button className="btn" type="button" disabled={busy !== null} onClick={() => void runAction('probe')}>
            {busy === 'probe' ? 'Probing…' : 'Re-verify grant (write probe)'}
          </button>
        )}
      </div>
      {showGuidance && guidance && (
        <div style={{ fontSize: 12, marginTop: 8 }}>
          <p style={{ margin: '4px 0' }}>
            <strong>Step 1 — admin consent.</strong> A customer Global Administrator consents the platform
            app&apos;s <code>Sites.Selected</code> application permission in their tenant:
          </p>
          <p style={{ margin: '4px 0', wordBreak: 'break-all' }}>
            <a href={guidance.adminConsentUrl} target="_blank" rel="noreferrer">{guidance.adminConsentUrl}</a>
          </p>
          <p style={{ margin: '8px 0 4px' }}>
            <strong>Step 2 — grant write on the audit site ONLY:</strong>{' '}
            <code style={{ wordBreak: 'break-all' }}>{guidance.siteUrl}</code>
          </p>
          <p style={{ margin: '4px 0' }}>
            Run this one-time command in customer-controlled administrator tooling. The ordinary Edinshall Portal is never granted <code>Sites.FullControl.All</code>.
          </p>
          <pre style={{ whiteSpace: 'pre-wrap', background: 'var(--surface-2, #f4f4f4)', padding: 8, borderRadius: 4 }}>
            {guidance.powershellSnippet}
          </pre>
          {grant.grantStatus !== 'revoked' && (
            <div style={{ display: 'grid', gap: 6, margin: '8px 0' }}>
              <label>
                Permission ID returned by the command
                <input className="input" value={permissionId} onChange={event => setPermissionId(event.target.value)} disabled={busy !== null} />
              </label>
              <button className="btn secondary" type="button" onClick={() => void recordAssignment()} disabled={busy !== null || !permissionId.trim()}>
                {busy === 'record-assignment' ? 'Recording…' : 'Record assignment identity'}
              </button>
            </div>
          )}
          <details>
            <summary>Raw Graph call</summary>
            <pre style={{ whiteSpace: 'pre-wrap', background: 'var(--surface-2, #f4f4f4)', padding: 8, borderRadius: 4 }}>
              {guidance.graphSnippet}
            </pre>
          </details>
          <p className="muted" style={{ margin: '4px 0' }}>
            Then click <strong>Verify grant (write probe)</strong> — the platform creates and immediately
            deletes a probe item to prove the write role before any audit is ever created server-side.
          </p>
          {grant.grantStatus === 'revoked' && (
            <>
              <p style={{ margin: '8px 0 4px' }}><strong>Microsoft 365 removal step.</strong> Remove only the Edinshall zero-touch assignment:</p>
              <pre style={{ whiteSpace: 'pre-wrap', background: 'var(--surface-2, #f4f4f4)', padding: 8, borderRadius: 4 }}>
                {guidance.revokePowershellSnippet}
              </pre>
            </>
          )}
        </div>
      )}
      {notice && <div className="muted" style={{ color: 'var(--success, #107c10)', fontSize: 12 }}>{notice}</div>}
      {error && <div className="muted" style={{ color: 'var(--danger)', fontSize: 12 }}>{error}</div>}
    </div>
  );
};

export default ZeroTouchCard;
